import React, { useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import type {
  NotificationChannel,
  NotificationChannelType,
  CreateNotificationChannelDto,
  UpdateNotificationChannelDto,
} from '../../types/notifications';
import {
  CHANNEL_TYPE_LABELS,
  CHANNEL_TYPE_ICONS,
} from '../../types/notifications';

interface NotificationChannelFormProps {
  channel?: NotificationChannel;
  onSubmit: (data: CreateNotificationChannelDto | UpdateNotificationChannelDto) => void;
  onCancel: () => void;
}

export function NotificationChannelForm({
  channel,
  onSubmit,
  onCancel,
}: NotificationChannelFormProps) {
  const [formData, setFormData] = useState({
    channel_type: channel?.channel_type || ('email' as NotificationChannelType),
    channel_value: channel?.channel_value || '',
    label: channel?.label || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    if (!channel && !formData.channel_value.trim()) {
      newErrors.channel_value = 'Value is required';
    }

    // Validate channel value format
    if (!channel && formData.channel_value) {
      if (formData.channel_type === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.channel_value)) {
          newErrors.channel_value = 'Invalid email address';
        }
      } else if (formData.channel_type === 'sms' || formData.channel_type === 'whatsapp') {
        const phoneRegex = /^\+?[1-9]\d{1,14}$/;
        if (!phoneRegex.test(formData.channel_value.replace(/\D/g, ''))) {
          newErrors.channel_value = 'Invalid phone number. Use format: +1234567890';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (channel) {
      // Update existing channel
      onSubmit({
        label: formData.label,
      });
    } else {
      // Create new channel
      let normalizedValue = formData.channel_value.trim();
      
      // Normalize phone numbers
      if (formData.channel_type === 'sms' || formData.channel_type === 'whatsapp') {
        normalizedValue = normalizedValue.replace(/\D/g, '');
        if (!normalizedValue.startsWith('+')) {
          normalizedValue = `+${normalizedValue}`;
        }
      }

      onSubmit({
        channel_type: formData.channel_type,
        channel_value: normalizedValue,
        label: formData.label,
      });
    }
  };

  const getPlaceholder = () => {
    switch (formData.channel_type) {
      case 'email':
        return 'user@example.com';
      case 'sms':
      case 'whatsapp':
        return '+1234567890';
      default:
        return '';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!channel && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Channel Type
          </label>
          <div className="grid grid-cols-3 gap-2">
            {(['email', 'sms', 'whatsapp'] as NotificationChannelType[]).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setFormData({ ...formData, channel_type: type })}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  formData.channel_type === type
                    ? 'border-primary bg-accent'
                    : 'border-border hover:border-input'
                }`}
              >
                <div className="text-2xl mb-1">{CHANNEL_TYPE_ICONS[type]}</div>
                <div className="text-sm font-medium">{CHANNEL_TYPE_LABELS[type]}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {!channel && (
        <div>
          <label htmlFor="channel_value" className="block text-sm font-medium text-foreground mb-1">
            {CHANNEL_TYPE_LABELS[formData.channel_type]} Address
          </label>
          <Input
            id="channel_value"
            type={formData.channel_type === 'email' ? 'email' : 'tel'}
            value={formData.channel_value}
            onChange={(e) => setFormData({ ...formData, channel_value: e.target.value })}
            placeholder={getPlaceholder()}
            error={errors.channel_value}
          />
          {(formData.channel_type === 'sms' || formData.channel_type === 'whatsapp') && (
            <p className="mt-1 text-xs text-muted-foreground">
              Include country code (e.g., +1 for US/Canada)
            </p>
          )}
        </div>
      )}

      <div>
        <label htmlFor="label" className="block text-sm font-medium text-foreground mb-1">
          Label
        </label>
        <Input
          id="label"
          type="text"
          value={formData.label}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          placeholder="e.g., Work Email, Personal Phone"
          error={errors.label}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          A friendly name to identify this channel
        </p>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {channel ? 'Update' : 'Create'} Channel
        </Button>
      </div>
    </form>
  );
} 